import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, Plus, Trash2, Download, Edit3, Search, Save, X,
  Percent, ChevronDown, ChevronUp, FilePlus, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';

const ITEM_TYPES = [
  { value: 'prestation', label: 'Prestation' },
  { value: 'produit', label: 'Produit' },
  { value: 'main_oeuvre', label: 'Main-d\'œuvre' },
  { value: 'deplacement', label: 'Frais de déplacement' },
];

const UNITS = [
  { value: 'forfait', label: 'forfait' },
  { value: 'h', label: 'heure' },
  { value: 'j', label: 'jour' },
  { value: 'u', label: 'unité' },
  { value: 'km', label: 'km' },
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'lot', label: 'lot' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Brouillon', color: 'bg-zinc-200 text-zinc-700' },
  { value: 'sent', label: 'Envoyé', color: 'bg-blue-100 text-blue-700' },
  { value: 'accepted', label: 'Accepté', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'refused', label: 'Refusé', color: 'bg-red-100 text-red-700' },
  { value: 'expired', label: 'Expiré', color: 'bg-amber-100 text-amber-700' },
];

const emptyClient = {
  company_name: '', first_name: '', last_name: '', address: '',
  postal_code: '', city: '', email: '', phone: '', siren: ''
};

const emptyForm = {
  number: '',
  date: new Date().toISOString().slice(0, 10),
  validity_days: 30,
  client: { ...emptyClient },
  project_start_date: '',
  project_duration: '',
  items: [],
  discounts: [],
  notes: '',
  tva_rate: 0,
  status: 'draft',
};

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function newItem() {
  return {
    id: uid(),
    type: 'prestation',
    title: '',
    description: '',
    quantity: 1,
    unit: 'forfait',
    unit_price_ht: 0,
    discount_label: '',
    discount_type: 'percent',
    discount_value: 0,
  };
}

function newDiscount() {
  return { id: uid(), label: '', reason: '', type: 'percent', value: 0 };
}

function fmtEUR(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function statusBadge(status) {
  const s = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.color}`}>
      {s.label}
    </span>
  );
}

export default function AdminCommercialQuotes() {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null); // null = create | object = edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/commercial-quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setQuotes(data.quotes || []);
    } catch (err) {
      toast.error('Impossible de charger les devis');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // ---- Totals computed live in the form ----
  const totals = useMemo(() => {
    let totalGross = 0;
    let totalLineDiscounts = 0;
    form.items.forEach((it) => {
      const lineGross = (Number(it.quantity) || 0) * (Number(it.unit_price_ht) || 0);
      totalGross += lineGross;
      const dv = Number(it.discount_value) || 0;
      if (dv > 0) {
        const ld = it.discount_type === 'percent' ? lineGross * (dv / 100) : dv;
        totalLineDiscounts += Math.min(ld, lineGross);
      }
    });
    const htAfterLines = Math.max(0, totalGross - totalLineDiscounts);

    let globalDiscount = 0;
    form.discounts.forEach((d) => {
      const v = Number(d.value) || 0;
      globalDiscount += d.type === 'percent' ? htAfterLines * (v / 100) : v;
    });
    const afterDiscount = Math.max(0, htAfterLines - globalDiscount);
    const tva = afterDiscount * ((Number(form.tva_rate) || 0) / 100);
    return {
      gross: totalGross,
      lineDiscount: totalLineDiscounts,
      ht: htAfterLines,
      discount: globalDiscount,
      afterDiscount,
      tva,
      ttc: afterDiscount + tva,
    };
  }, [form]);

  // ---- Filters ----
  const filteredQuotes = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return quotes;
    return quotes.filter((quote) => {
      const c = quote.client || {};
      const haystack = [
        quote.number, c.company_name, c.first_name, c.last_name, c.email, c.city
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [quotes, search]);

  // ---- Open editor ----
  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, items: [newItem()] });
    setShowEditor(true);
  };

  const openEdit = (quote) => {
    setEditing(quote);
    setForm({
      number: quote.number || '',
      date: quote.date || new Date().toISOString().slice(0, 10),
      validity_days: quote.validity_days || 30,
      client: { ...emptyClient, ...(quote.client || {}) },
      project_start_date: quote.project_start_date || '',
      project_duration: quote.project_duration || '',
      items: (quote.items || []).map((it) => ({ ...it })),
      discounts: (quote.discounts || []).map((d) => ({ ...d })),
      notes: quote.notes || '',
      tva_rate: quote.tva_rate || 0,
      status: quote.status || 'draft',
    });
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditing(null);
    setForm(emptyForm);
  };

  // ---- Form helpers ----
  const updateClient = (field, value) => {
    setForm((f) => ({ ...f, client: { ...f.client, [field]: value } }));
  };
  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };
  const updateItem = (id, field, value) => {
    setForm((f) => ({ ...f, items: f.items.map((it) => it.id === id ? { ...it, [field]: value } : it) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, newItem()] }));
  const removeItem = (id) => setForm((f) => ({ ...f, items: f.items.filter((it) => it.id !== id) }));

  const updateDiscount = (id, field, value) => {
    setForm((f) => ({ ...f, discounts: f.discounts.map((d) => d.id === id ? { ...d, [field]: value } : d) }));
  };
  const addDiscount = () => setForm((f) => ({ ...f, discounts: [...f.discounts, newDiscount()] }));
  const removeDiscount = (id) => setForm((f) => ({ ...f, discounts: f.discounts.filter((d) => d.id !== id) }));

  // ---- Save ----
  const save = async () => {
    if (form.items.length === 0) { toast.error('Ajoutez au moins une ligne'); return; }
    if (form.items.some((it) => !it.title.trim())) { toast.error('Chaque ligne doit avoir une désignation'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        validity_days: Number(form.validity_days) || 30,
        tva_rate: Number(form.tva_rate) || 0,
        items: form.items.map((it) => ({
          id: it.id,
          type: it.type,
          title: it.title,
          description: it.description || '',
          quantity: Number(it.quantity) || 0,
          unit: it.unit,
          unit_price_ht: Number(it.unit_price_ht) || 0,
          discount_label: it.discount_label || '',
          discount_type: it.discount_type || 'percent',
          discount_value: Number(it.discount_value) || 0,
        })),
        discounts: form.discounts.map((d) => ({
          id: d.id,
          label: d.label,
          reason: d.reason || '',
          type: d.type,
          value: Number(d.value) || 0,
        })),
      };
      const url = editing
        ? `${API_URL}/api/admin/commercial-quotes/${editing.id}`
        : `${API_URL}/api/admin/commercial-quotes`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de l\'enregistrement');
      }
      toast.success(editing ? 'Devis mis à jour' : 'Devis créé');
      closeEditor();
      fetchQuotes();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- Actions ----
  const remove = async (quote) => {
    if (!window.confirm(`Supprimer le devis ${quote.number} ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/commercial-quotes/${quote.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Suppression impossible');
      toast.success('Devis supprimé');
      fetchQuotes();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const duplicate = (quote) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      date: new Date().toISOString().slice(0, 10),
      validity_days: quote.validity_days || 30,
      client: { ...emptyClient, ...(quote.client || {}) },
      project_start_date: quote.project_start_date || '',
      project_duration: quote.project_duration || '',
      items: (quote.items || []).map((it) => ({ ...it, id: uid() })),
      discounts: (quote.discounts || []).map((d) => ({ ...d, id: uid() })),
      notes: quote.notes || '',
      tva_rate: quote.tva_rate || 0,
      status: 'draft',
    });
    setShowEditor(true);
  };

  const downloadPdf = async (quote) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/commercial-quotes/${quote.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur lors du téléchargement');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quote.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ============== RENDER ==============
  return (
    <div className="space-y-6" data-testid="admin-commercial-quotes">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Devis commerciaux</h1>
          <p className="text-sm text-muted-foreground">Génération de devis PDF (papier) – distinct du système de devis Stripe.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-quote-btn" className="btn-primary">
          <Plus className="h-4 w-4 mr-2" /> Nouveau devis
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher par numéro, client, ville…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-quotes"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-muted-foreground">Chargement…</div>
      ) : filteredQuotes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>Aucun devis pour l'instant. Cliquez sur « Nouveau devis » pour commencer.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredQuotes.map((quote) => {
            const c = quote.client || {};
            const clientLabel = c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Sans client';
            const isOpen = expandedId === quote.id;
            return (
              <Card key={quote.id} data-testid={`quote-${quote.number}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => setExpandedId(isOpen ? null : quote.id)}
                        className="mt-1 text-muted-foreground hover:text-foreground"
                        data-testid={`toggle-${quote.number}`}
                      >
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold">{quote.number}</span>
                          {statusBadge(quote.status)}
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5">
                          {clientLabel} · {new Date(quote.date).toLocaleDateString('fr-FR')} · {fmtEUR(quote.total_ttc)} TTC
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(quote)} data-testid={`pdf-${quote.number}`}>
                        <Download className="h-4 w-4 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(quote)} data-testid={`edit-${quote.number}`}>
                        <Edit3 className="h-4 w-4 mr-1" /> Modifier
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => duplicate(quote)} data-testid={`duplicate-${quote.number}`}>
                        <Copy className="h-4 w-4 mr-1" /> Dupliquer
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => remove(quote)} data-testid={`delete-${quote.number}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-4 pt-4 border-t border-border/60 grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="font-semibold mb-2">Client</div>
                        {c.company_name && <div>{c.company_name}</div>}
                        <div className="text-muted-foreground">
                          {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                        </div>
                        <div className="text-muted-foreground">{c.address}</div>
                        <div className="text-muted-foreground">{c.postal_code} {c.city}</div>
                        {c.email && <div className="text-muted-foreground">{c.email}</div>}
                      </div>
                      <div>
                        <div className="font-semibold mb-2">Récapitulatif</div>
                        <div className="space-y-1 text-muted-foreground">
                          <div>Total HT : <span className="text-foreground font-medium">{fmtEUR(quote.total_ht)}</span></div>
                          {quote.total_discount > 0 && (
                            <div className="text-red-600">Remise : − {fmtEUR(quote.total_discount)}</div>
                          )}
                          <div>TVA ({quote.tva_rate}%) : <span className="text-foreground font-medium">{fmtEUR(quote.total_tva)}</span></div>
                          <div className="font-bold text-foreground">Total TTC : {fmtEUR(quote.total_ttc)}</div>
                          <div className="pt-1">Validité : {quote.validity_days} jours</div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ============== EDITOR MODAL ============== */}
      {showEditor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-background w-full max-w-5xl rounded-2xl my-8 shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b bg-background rounded-t-2xl">
              <div className="flex items-center gap-2">
                <FilePlus className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">
                  {editing ? `Modifier le devis ${editing.number}` : 'Nouveau devis'}
                </h2>
              </div>
              <button onClick={closeEditor} className="p-2 hover:bg-muted rounded-lg" data-testid="close-editor">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* ---- Quote meta ---- */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Informations du devis</CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Numéro {editing ? '' : <span className="text-xs text-muted-foreground">(auto si vide)</span>}</Label>
                    <Input
                      value={form.number}
                      onChange={(e) => updateField('number', e.target.value)}
                      placeholder="DEV-2026-0001"
                      data-testid="quote-number"
                    />
                  </div>
                  <div>
                    <Label>Date du devis</Label>
                    <Input type="date" value={form.date} onChange={(e) => updateField('date', e.target.value)} data-testid="quote-date" />
                  </div>
                  <div>
                    <Label>Validité (jours)</Label>
                    <Input type="number" min="1" value={form.validity_days} onChange={(e) => updateField('validity_days', e.target.value)} data-testid="quote-validity" />
                  </div>
                  <div>
                    <Label>Début de prestation (optionnel)</Label>
                    <Input type="date" value={form.project_start_date} onChange={(e) => updateField('project_start_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Durée de prestation (optionnel)</Label>
                    <Input value={form.project_duration} onChange={(e) => updateField('project_duration', e.target.value)} placeholder="3 semaines" />
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <select
                      value={form.status}
                      onChange={(e) => updateField('status', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                    </select>
                  </div>
                </CardContent>
              </Card>

              {/* ---- Client ---- */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Client</CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Société (optionnel)</Label>
                    <Input value={form.client.company_name} onChange={(e) => updateClient('company_name', e.target.value)} placeholder="Société ABC" />
                  </div>
                  <div><Label>Prénom</Label><Input value={form.client.first_name} onChange={(e) => updateClient('first_name', e.target.value)} /></div>
                  <div><Label>Nom</Label><Input value={form.client.last_name} onChange={(e) => updateClient('last_name', e.target.value)} /></div>
                  <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.client.address} onChange={(e) => updateClient('address', e.target.value)} /></div>
                  <div><Label>Code postal</Label><Input value={form.client.postal_code} onChange={(e) => updateClient('postal_code', e.target.value)} /></div>
                  <div><Label>Ville</Label><Input value={form.client.city} onChange={(e) => updateClient('city', e.target.value)} /></div>
                  <div><Label>Email</Label><Input type="email" value={form.client.email} onChange={(e) => updateClient('email', e.target.value)} /></div>
                  <div><Label>Téléphone</Label><Input value={form.client.phone} onChange={(e) => updateClient('phone', e.target.value)} /></div>
                  <div><Label>SIREN (optionnel)</Label><Input value={form.client.siren} onChange={(e) => updateClient('siren', e.target.value)} /></div>
                </CardContent>
              </Card>

              {/* ---- Items ---- */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base">Lignes du devis</CardTitle>
                  <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item">
                    <Plus className="h-4 w-4 mr-1" /> Ligne
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {form.items.length === 0 && (
                    <p className="text-sm text-muted-foreground">Aucune ligne. Ajoutez-en une via le bouton ci-dessus.</p>
                  )}
                  {form.items.map((item, idx) => (
                    <div key={item.id} className="border border-border rounded-xl p-4 bg-muted/30 space-y-3" data-testid={`item-${idx}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-bold text-muted-foreground uppercase">Ligne {idx + 1}</span>
                        <button onClick={() => removeItem(item.id)} className="text-destructive hover:bg-destructive/10 p-1.5 rounded-md" data-testid={`remove-item-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid md:grid-cols-[1fr_180px] gap-3">
                        <div>
                          <Label>Désignation *</Label>
                          <Input value={item.title} onChange={(e) => updateItem(item.id, 'title', e.target.value)} placeholder="Création du site web" />
                        </div>
                        <div>
                          <Label>Type</Label>
                          <select
                            value={item.type}
                            onChange={(e) => updateItem(item.id, 'type', e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            {ITEM_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label>Description (optionnel)</Label>
                        <textarea
                          value={item.description}
                          onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          placeholder="- Maquette UI/UX\n- 3 pages : accueil, services, contact\n- Hébergement non inclus"
                          rows={3}
                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed font-mono"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Astuce : commencez une ligne par « - » ou « • » pour créer une puce dans le PDF.</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label>Quantité</Label>
                          <Input type="number" step="0.01" value={item.quantity} onChange={(e) => updateItem(item.id, 'quantity', e.target.value)} />
                        </div>
                        <div>
                          <Label>Unité</Label>
                          <select
                            value={item.unit}
                            onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            {UNITS.map((u) => (<option key={u.value} value={u.value}>{u.label}</option>))}
                          </select>
                        </div>
                        <div>
                          <Label>PU HT (€)</Label>
                          <Input type="number" step="0.01" value={item.unit_price_ht} onChange={(e) => updateItem(item.id, 'unit_price_ht', e.target.value)} />
                        </div>
                        <div>
                          <Label>Total HT</Label>
                          <div className="h-10 flex items-center px-3 rounded-md bg-background border border-input text-sm font-semibold">
                            {(() => {
                              const gross = (Number(item.quantity) || 0) * (Number(item.unit_price_ht) || 0);
                              const dv = Number(item.discount_value) || 0;
                              const ld = dv > 0 ? (item.discount_type === 'percent' ? gross * (dv / 100) : dv) : 0;
                              return fmtEUR(Math.max(0, gross - ld));
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Remise par ligne */}
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px] gap-3 pt-3 border-t border-border/50">
                        <div>
                          <Label className="text-xs flex items-center gap-1.5">
                            <Percent className="h-3 w-3" /> Remise sur cette ligne (optionnel)
                          </Label>
                          <Input
                            placeholder="Nom de la remise (ex. Geste commercial)"
                            value={item.discount_label || ''}
                            onChange={(e) => updateItem(item.id, 'discount_label', e.target.value)}
                            data-testid={`item-${idx}-discount-label`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Type</Label>
                          <select
                            value={item.discount_type || 'percent'}
                            onChange={(e) => updateItem(item.id, 'discount_type', e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="percent">% pourcent</option>
                            <option value="amount">€ montant</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Valeur</Label>
                          <Input
                            type="number" step="0.01"
                            value={item.discount_value || 0}
                            onChange={(e) => updateItem(item.id, 'discount_value', e.target.value)}
                            data-testid={`item-${idx}-discount-value`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* ---- Discounts ---- */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-base flex items-center gap-2"><Percent className="h-4 w-4" /> Réductions</CardTitle>
                  <Button size="sm" variant="outline" onClick={addDiscount} data-testid="add-discount">
                    <Plus className="h-4 w-4 mr-1" /> Remise
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {form.discounts.length === 0 && (
                    <p className="text-sm text-muted-foreground">Aucune réduction. Cliquez sur « Remise » pour en ajouter.</p>
                  )}
                  {form.discounts.map((d, idx) => (
                    <div key={d.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_140px_40px] gap-2 items-end" data-testid={`discount-${idx}`}>
                      <div>
                        <Label>Nom de la remise</Label>
                        <Input value={d.label} onChange={(e) => updateDiscount(d.id, 'label', e.target.value)} placeholder="Remise fidélité" />
                      </div>
                      <div>
                        <Label>Motif (optionnel)</Label>
                        <Input value={d.reason} onChange={(e) => updateDiscount(d.id, 'reason', e.target.value)} placeholder="5e projet" />
                      </div>
                      <div>
                        <Label>Type</Label>
                        <select
                          value={d.type}
                          onChange={(e) => updateDiscount(d.id, 'type', e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="percent">% (pourcentage)</option>
                          <option value="amount">€ (montant)</option>
                        </select>
                      </div>
                      <div>
                        <Label>Valeur</Label>
                        <Input type="number" step="0.01" value={d.value} onChange={(e) => updateDiscount(d.id, 'value', e.target.value)} />
                      </div>
                      <button onClick={() => removeDiscount(d.id)} className="text-destructive hover:bg-destructive/10 p-2 rounded-md h-10 self-end">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* ---- Notes ---- */}
              <Card>
                <CardHeader><CardTitle className="text-base">Informations complémentaires (optionnel)</CardTitle></CardHeader>
                <CardContent>
                  <textarea
                    value={form.notes}
                    onChange={(e) => updateField('notes', e.target.value)}
                    placeholder="Ex.&#10;- Hébergement non inclus dans ce devis&#10;- Formation 1h offerte à la mise en ligne&#10;- Acompte 30% à la signature"
                    rows={5}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Astuce : commencez une ligne par « - » ou « • » pour créer une puce dans le PDF.</p>
                </CardContent>
              </Card>

              {/* ---- TVA + Totals ---- */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-[160px_1fr] gap-4 items-start">
                    <div>
                      <Label>TVA (%)</Label>
                      <Input type="number" step="0.01" value={form.tva_rate} onChange={(e) => updateField('tva_rate', e.target.value)} />
                      <p className="text-xs text-muted-foreground mt-1">0% par défaut (art. 293 B du CGI)</p>
                    </div>
                    <div className="bg-muted/50 rounded-xl p-4 space-y-1 text-sm">
                      {totals.lineDiscount > 0 && (
                        <>
                          <div className="flex justify-between"><span className="text-muted-foreground">Sous-total brut</span><span className="font-semibold">{fmtEUR(totals.gross)}</span></div>
                          <div className="flex justify-between text-red-600"><span>Remises sur lignes</span><span>− {fmtEUR(totals.lineDiscount)}</span></div>
                        </>
                      )}
                      <div className="flex justify-between"><span className="text-muted-foreground">Total HT</span><span className="font-semibold">{fmtEUR(totals.ht)}</span></div>
                      {totals.discount > 0 && (
                        <>
                          <div className="flex justify-between text-red-600"><span>Réduction globale</span><span>− {fmtEUR(totals.discount)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Après remise</span><span className="font-semibold">{fmtEUR(totals.afterDiscount)}</span></div>
                        </>
                      )}
                      <div className="flex justify-between"><span className="text-muted-foreground">TVA ({Number(form.tva_rate || 0)}%)</span><span className="font-semibold">{fmtEUR(totals.tva)}</span></div>
                      <div className="flex justify-between pt-2 mt-1 border-t border-border text-base"><span className="font-bold">Total TTC</span><span className="font-bold text-primary">{fmtEUR(totals.ttc)}</span></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-background border-t px-6 py-4 flex justify-end gap-2 rounded-b-2xl">
              <Button variant="outline" onClick={closeEditor}>Annuler</Button>
              <Button onClick={save} disabled={saving} className="btn-primary" data-testid="save-quote">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Enregistrement…' : (editing ? 'Mettre à jour' : 'Créer le devis')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
